import React from "react";
import { Button } from "../ui/button.tsx";

type ExampleChipProps = {
  label: string;
  prefill: string;
  setInputPrefill: (prefill: string) => void;
};
/**
 * Small pill button used in empty states to prefill the input with an example
 * and switch the mode. Improves guidance for first-time users.
 */
const ExampleChip: React.FC<ExampleChipProps> = ({ label, prefill, setInputPrefill }) => {
  return (
    <Button
      variant="outline"
      onClick={() => {
        setInputPrefill(prefill);
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      data-statsig-name="thread-empty-example-chip"
    >
      {label}
    </Button>
  );
};

const AgentSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="114"
    height="114"
    viewBox="0 0 114 114"
    fill="none"
    role="img"
    aria-label="Sparkles"
  >
    <path
      d="M45.4242 74.3565L41.5625 87.875L37.7008 74.3565C36.7026 70.8646 34.8313 67.6846 32.2632 65.1165C29.6952 62.5485 26.5152 60.6771 23.0233 59.679L9.5 55.8125L23.0185 51.9508C26.5104 50.9526 29.6904 49.0813 32.2585 46.5132C34.8265 43.9452 36.6979 40.7652 37.696 37.2733L41.5625 23.75L45.4242 37.2685C46.4224 40.7604 48.2937 43.9404 50.8618 46.5085C53.4298 49.0765 56.6098 50.9479 60.1017 51.946L73.625 55.8125L60.1065 59.6742C56.6146 60.6724 53.4346 62.5437 50.8665 65.1118C48.2985 67.6798 46.4271 70.8598 45.429 74.3517L45.4242 74.3565ZM85.5428 40.2088L84.3125 45.125L83.0822 40.2088C82.3781 37.39 80.9211 34.8157 78.8671 32.7609C76.8132 30.7061 74.2394 29.2481 71.421 28.5427L66.5 27.3125L71.421 26.0823C74.2394 25.3769 76.8132 23.9189 78.8671 21.8641C80.9211 19.8093 82.3781 17.235 83.0822 14.4163L84.3125 9.5L85.5428 14.4163C86.2473 17.2355 87.705 19.8103 89.7599 21.8651C91.8147 23.92 94.3895 25.3777 97.2088 26.0823L102.125 27.3125L97.2088 28.5427C94.3895 29.2473 91.8147 30.705 89.7599 32.7599C87.705 34.8147 86.2473 37.3895 85.5428 40.2088ZM79.059 96.5057L77.1875 102.125L75.316 96.5057C74.7913 94.9317 73.9074 93.5015 72.7342 92.3283C71.561 91.1551 70.1308 90.2712 68.5567 89.7465L62.9375 87.875L68.5567 86.0035C70.1308 85.4788 71.561 84.5949 72.7342 83.4217C73.9074 82.2485 74.7913 80.8183 75.316 79.2443L77.1875 73.625L79.059 79.2443C79.5837 80.8183 80.4676 82.2485 81.6408 83.4217C82.814 84.5949 84.2442 85.4788 85.8182 86.0035L91.4375 87.875L85.8182 89.7465C84.2442 90.2712 82.814 91.1551 81.6408 92.3283C80.4676 93.5015 79.5837 94.9317 79.059 96.5057Z"
      stroke="url(#paint0_linear_3762_32319)"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_3762_32319"
        x1="25.5562"
        y1="18.0562"
        x2="78.1375"
        y2="79.5625"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#5F79F5" />
        <stop offset="1" stopColor="#C084FC" />
      </linearGradient>
    </defs>
  </svg>
);

export const EmptyChatPlaceholder = ({
  show,
  setInputPrefill,
}: {
  show: boolean;
  setInputPrefill: (prefill: string) => void;
}) => {
  if (!show) return null;

  return (
    <div className="flex flex-col items-center gap-6 w-full min-h-[336px]">
      <div className="flex flex-col items-center gap-4 shrink-0">
        <div className="size-[180px] rounded-full bg-[rgba(217,217,217,0.2)] backdrop-blur-[7px] backdrop-filter flex items-center justify-center shrink-0">
          <AgentSVG />
        </div>
        <div className="font-serif text-2xl leading-8 text-zinc-900">Try something new</div>
      </div>

      {/* Example chips section */}
      <div className="flex flex-wrap items-center justify-center gap-3 md:w-[710px] shrink-0">
        <ExampleChip
          label="Schedule a meeting"
          prefill="Schedule a meeting"
          setInputPrefill={setInputPrefill}
        />
        <ExampleChip
          label="Set up a 1:1"
          prefill="Set up a 1:1"
          setInputPrefill={setInputPrefill}
        />
        <ExampleChip label="Grab coffee" prefill="Grab coffee" setInputPrefill={setInputPrefill} />
        <ExampleChip
          label="Coordinate lunch"
          prefill="Coordinate lunch"
          setInputPrefill={setInputPrefill}
        />
        <ExampleChip
          label="Schedule a team sync"
          prefill="Schedule a team sync"
          setInputPrefill={setInputPrefill}
        />
      </div>
    </div>
  );
};
